import { useEffect, useRef } from 'react';
import { message } from 'antd';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import useSearchParams from '../../../../core/hooks/useSearchParams';

export const useTaskExport = () => {
  const { fetchAllTasks, allTasksData, allTasksLoading, currentUser, showMyTasks } =
    useTaskContext();

  const [searchParams] = useSearchParams({
    assigneeNames: '',
    requestor: '',
    requestTypes: '',
    requestSubTypes: '',
    status: '',
    advertiserTypes: '',
    advertiser: '',
    name: '',
    urgency: '',
    showMyTasks: showMyTasks,
    following: false,
    collaborating: false,
    dueDate: '',
  });

  const isExporting = useRef(false);

  useEffect(() => {
    if (isExporting.current && allTasksData && allTasksData.tasksConnection) {
      message.destroy();
      const allTasks = allTasksData.tasksConnection.edges.map((edge) => edge.node);
      exportTasksToXLSX(allTasks, message);
      isExporting.current = false;
    } else if (isExporting.current && allTasksData && allTasksData.tasksConnection === null) {
      message.destroy();
      message.warning('No tasks found matching current filters');
      isExporting.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTasksData]);

  useEffect(() => {
    if (isExporting.current && !allTasksLoading && allTasksData === undefined) {
      message.destroy();
      isExporting.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTasksLoading, allTasksData]);

  const buildTaskQueryParams = (searchParams, currentUser, showMyTasks) => {
    const assigneeNamesArray = searchParams.assigneeNames
      ? searchParams.assigneeNames.split(',')
      : [];
    const requestorArray = searchParams.requestor ? searchParams.requestor.split(',') : [];
    const requestTypesArray = searchParams.requestTypes ? searchParams.requestTypes.split(',') : [];
    const requestSubTypesArray = searchParams.requestSubTypes
      ? searchParams.requestSubTypes.split(',')
      : [];
    const statusArray = searchParams.status ? searchParams.status.split(',') : [];
    const advertiserTypesArray = searchParams.advertiserTypes
      ? searchParams.advertiserTypes.split(',')
      : [];
    const advertiserArray = searchParams.advertiser ? searchParams.advertiser.split(',') : [];
    const urgencyArray = searchParams.urgency ? searchParams.urgency.split(',') : [];

    let dueDateStart = null;
    let dueDateEnd = null;
    if (searchParams.dueDate && searchParams.dueDate.split(',').length === 2) {
      [dueDateStart, dueDateEnd] = searchParams.dueDate.split(',');
    }

    const following = searchParams.following === true || searchParams.following === 'true';
    const collaborating =
      searchParams.collaborating === true || searchParams.collaborating === 'true';

    return {
      AND: [
        { name_contains: searchParams.name || '' },
        ...(showMyTasks
          ? [
              {
                OR: [
                  { assigneeUser: { id: currentUser.id } },
                  { assigneeUsers_some: { id: currentUser.id } },
                  { createdBy: { id: currentUser.id } },
                  { followers_some: { id: currentUser.id } },
                  { collaborators_some: { id: currentUser.id } },
                ],
              },
            ]
          : []),
        ...(assigneeNamesArray.length
          ? [
              {
                OR: [
                  {
                    assigneeUsers_some: {
                      id_in: assigneeNamesArray,
                    },
                  },
                  { assigneeUser: { id_in: assigneeNamesArray } },
                ],
              },
            ]
          : []),
        ...(requestorArray.length
          ? [
              {
                createdBy: {
                  id_in: requestorArray,
                },
              },
            ]
          : []),
        ...(requestTypesArray.length ? [{ requestType_in: requestTypesArray }] : []),
        ...(requestSubTypesArray.length ? [{ requestSubType_in: requestSubTypesArray }] : []),
        ...(statusArray.length ? [{ status_in: statusArray }] : []),
        ...(advertiserTypesArray.length
          ? [
              {
                createdBy: {
                  roles_some: { org: { level_in: advertiserTypesArray } },
                },
              },
            ]
          : []),
        ...(advertiserArray.length
          ? [
              {
                createdBy: {
                  roles_some: { org: { name_in: advertiserArray } },
                },
              },
            ]
          : []),
        ...(urgencyArray.length ? [{ urgency_in: urgencyArray }] : []),
        ...(dueDateStart && dueDateEnd
          ? [
              {
                dueDate_gte: dueDateStart,
                dueDate_lte: dueDateEnd,
              },
            ]
          : []),
        ...(following || collaborating
          ? [
              {
                OR: [
                  ...(following ? [{ followers_some: { id: currentUser.id } }] : []),
                  ...(collaborating ? [{ collaborators_some: { id: currentUser.id } }] : []),
                ],
              },
            ]
          : []),
        {
          OR: [
            {
              createdBy: {
                roles_some: {
                  org: {
                    OR: [
                      {
                        parentOrg_some: {
                          id: currentUser.role.org.id,
                        },
                      },
                      {
                        id: currentUser.role.org.id,
                      },
                    ],
                  },
                },
              },
            },
          ],
        },
      ],
    };
  };

  const exportTasks = () => {
    if (allTasksLoading || isExporting.current) {
      message.warning('Export already in progress, please wait...');
      return;
    }

    const where = buildTaskQueryParams(searchParams, currentUser, showMyTasks);

    isExporting.current = true;
    message.loading('Fetching all tasks for export...', 0);

    fetchAllTasks({
      variables: {
        where: where,
        orderBy: 'updatedAt_DESC',
        first: 10000,
      },
    });
  };

  const exportTasksToXLSX = (tasks, message) => {
    if (!tasks || tasks.length === 0) {
      message.warning('No tasks to export');
      return;
    }

    import('xlsx')
      .then((XLSX) => {
        // Convert tasks to worksheet rows
        const rows = tasks.map((taskItem) => {
          const assignees =
            taskItem.assigneeUsers && taskItem.assigneeUsers.length
              ? taskItem.assigneeUsers.map((user) => user.name || user.username).join('; ')
              : taskItem.assigneeUser
                ? taskItem.assigneeUser.name || taskItem.assigneeUser.username
                : '';

          const advertiserType =
            taskItem.createdBy &&
            taskItem.createdBy.roles &&
            taskItem.createdBy.roles[0] &&
            taskItem.createdBy.roles[0].org &&
            taskItem.createdBy.roles[0].org.level
              ? taskItem.createdBy.roles[0].org.level
                  .split('_')
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')
              : '';

          const advertiser =
            taskItem.createdBy &&
            taskItem.createdBy.roles &&
            taskItem.createdBy.roles[0] &&
            taskItem.createdBy.roles[0].org &&
            taskItem.createdBy.roles[0].org.name
              ? taskItem.createdBy.roles[0].org.name
              : '';

          const status = taskItem.status
            ? taskItem.status === 'READY'
              ? 'READY FOR REVIEW'
              : taskItem.status
                  .split('_')
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')
            : '';

          return {
            NAME: taskItem.name || '',
            'DUE DATE': taskItem.dueDate
              ? new Date(taskItem.dueDate).toLocaleDateString('en-CA')
              : '',
            ASSIGNEE: assignees,
            REQUESTOR: (taskItem.createdBy && taskItem.createdBy.name) || '',
            "REQUESTOR'S ORG": advertiser,
            "REQUESTOR'S ORG TYPE": advertiserType,
            'REQUEST TYPE': taskItem.requestType || '',
            'REQUEST SUB TYPE': taskItem.requestSubType || '',
            STATUS: status,
            URGENCY: taskItem.urgency || '',
            'CHANNEL PARTNER': (taskItem.channelPartner && taskItem.channelPartner.name) || '',
            ADVERTISER: (taskItem.advertiser && taskItem.advertiser.name) || '',
            'CREATED DATE': taskItem.createdAt
              ? new Date(taskItem.createdAt).toLocaleDateString()
              : '',
            'LAST UPDATED': taskItem.updatedAt
              ? new Date(taskItem.updatedAt).toLocaleDateString()
              : '',
          };
        });

        // Create the file
        const worksheet = XLSX.utils.json_to_sheet(rows);

        // column widths
        worksheet['!cols'] = [
          { wch: 30 }, // NAME
          { wch: 12 }, // DUE DATE
          { wch: 25 }, // ASSIGNEE
          { wch: 25 }, // REQUESTOR
          { wch: 25 }, // REQUESTOR'S ORG
          { wch: 20 }, // REQUESTOR'S ORG TYPE
          { wch: 20 }, // REQUEST TYPE
          { wch: 20 }, // REQUEST SUB TYPE
          { wch: 20 }, // STATUS
          { wch: 12 }, // URGENCY
          { wch: 25 }, // CHANNEL PARTNER
          { wch: 25 }, // ADVERTISER
          { wch: 15 }, // CREATED DATE
          { wch: 15 }, // LAST UPDATED
        ];

        // create the workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Tasks');

        // Generate & download
        XLSX.writeFile(workbook, `tasks_export_${new Date().toISOString().split('T')[0]}.xlsx`);

        message.success(`${tasks.length} tasks exported successfully!`);
      })
      .catch((error) => {
        console.error('Failed to load xlsx library:', error);
        message.error('Failed to export tasks');
      });
  };

  return {
    exportTasks,
    isExporting: allTasksLoading,
  };
};
